import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, MapPin } from "lucide-react";
import { searchAddress, type SearchResult } from "@/lib/geocode";
import { t } from "@/lib/i18n";

/**
 * Address search bar using Nominatim (OpenStreetMap geocoding).
 * Appears as an overlay at the top-center of the map.
 *
 * Props:
 *   onSelect — called with [lng, lat] when a result is selected
 */
export interface SearchBarProps {
  onSelect: (lng: number, lat: number) => void;
}

export function SearchBar({ onSelect }: SearchBarProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search
  const doSearch = useCallback(async (q: string) => {
    if (q.trim().length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const hits = await searchAddress(q);
      setResults(hits);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (value: string) => {
    setQuery(value);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => void doSearch(value), 400);
  };

  const handleSelect = (result: SearchResult) => {
    onSelect(result.lng, result.lat);
    setQuery(result.displayName.split(",")[0]);
    setOpen(false);
    setResults([]);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute z-20 left-3 right-3 top-[calc(env(safe-area-inset-top,0px)+3.5rem)] sm:left-[13rem] sm:right-3 sm:top-[calc(env(safe-area-inset-top,0px)+0.75rem)] sm:max-w-[min(50vw,380px)]"
    >
      <div className="flex items-center gap-2 rounded-full bg-bg-elevated/95 px-3 py-2 shadow-lg ring-1 ring-line backdrop-blur">
        <Search size={18} className="shrink-0 text-fg-muted" />
        <input
          type="text"
          inputMode="search"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={t("search.placeholder")}
          className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setResults([]);
            }}
            className="shrink-0 p-1 text-fg-muted hover:text-fg"
            aria-label={t("search.clear")}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {open && (results.length > 0 || loading) && (
        <div className="mt-1 max-h-[300px] overflow-y-auto rounded-xl bg-bg-elevated/95 shadow-xl ring-1 ring-line backdrop-blur">
          {loading ? (
            <div className="px-4 py-3 text-sm text-fg-muted">
              {t("search.loading")}
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.placeId}
                type="button"
                onClick={() => handleSelect(r)}
                className="flex w-full items-start gap-2 border-b border-line/50 px-3 py-2.5 text-left last:border-0 hover:bg-surface/60"
              >
                <MapPin size={16} className="mt-0.5 shrink-0 text-accent" />
                <div className="min-w-0">
                  <p className="truncate text-sm text-fg">
                    {r.displayName.split(",")[0]}
                  </p>
                  <p className="truncate text-xs text-fg-muted">
                    {r.displayName.split(",").slice(1).join(",").trim()}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
